import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import MillesimaLink from '@/components/common/MillesimaLink';
import Tile from '@/components/common/Tile';
import ProductCardLink from '@/components/listing/ProductCardLink';
import { ATTRIBUTES } from '@/constants/product';
import { useAppSelector } from '@/redux';
import { formatUrl, getHrefForCategLink } from '@/utils/linkUtils';
import { getAllProductsBrandLabel } from '@/utils/productCardUtils';
import { getAttribute, getAttributeValue } from '@/utils/productUtils';
import styles from './ProductVintageBox.module.scss';

const ProductVintageBox = ({ product, isDesktop = true, withBrandLink = true }) => {
	const { t } = useTranslation();
	const { storeConf } = useAppSelector(({ root }) => root);
	const { url: brandUrl, value: brand, isSpirit, groupIdentifier } = getAttribute(product, ATTRIBUTES.PRODUCER);
	const currentMillesime = getAttributeValue(product, ATTRIBUTES.MILLESIME);
	const regionValue = getAttributeValue(product, ATTRIBUTES.REGION);
	const { otherMillesimeForColor: millesimes } = product;
	const absoluteUrl = formatUrl(brandUrl, isSpirit, storeConf);

	const getAllProductBrandLabelLink = () =>
		brand &&
		brandUrl && (
			<MillesimaLink
				id="branded-label"
				href={getHrefForCategLink(brandUrl, groupIdentifier, isSpirit)}
				as={absoluteUrl}
				ariaLabel={getAllProductsBrandLabel(t, regionValue, brand, isSpirit)}
				innerHtml={getAllProductsBrandLabel(t, regionValue, brand, isSpirit)}
				customClassName={{ container: styles['brand-label-link'] }}
			/>
		);

	const getTileComponent = (value) => <Tile content={{ paragraph: value }} centered />;

	return (
		<>
			{millesimes?.length > 1 && (
				<div className={styles.container} data-test-id="product-vintage-box">
					{isDesktop && (
						<div className={styles.title} data-test-id="title">
							{t('product.othersVintages').toUpperCase()}
						</div>
					)}
					<div className={styles.tags} data-test-id="others-vintages">
						{millesimes.map((elem, key) => {
							const millesimeValue = elem.attributes.millesime.value;

							if (millesimeValue !== currentMillesime) {
								const { _id, seoKeyword } = elem;

								if (elem.nourl) {
									return (
										<a key={key} href="#" aria-label={millesimeValue}>
											{getTileComponent(millesimeValue)}
										</a>
									);
								}

								return (
									<ProductCardLink
										key={key}
										id={_id}
										seoKeyword={seoKeyword}
										storeConf={storeConf}
										isSpirit={product.isSpirit}
										ariaLabel={millesimeValue}
									>
										{getTileComponent(millesimeValue)}
									</ProductCardLink>
								);
							}
						})}
					</div>
				</div>
			)}

			{withBrandLink && getAllProductBrandLabelLink()}
		</>
	);
};

ProductVintageBox.propTypes = {
	product: PropTypes.object.isRequired,
	isDesktop: PropTypes.bool,
};

export default ProductVintageBox;
